import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  signal,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  FieldTypeConfig,
  FieldWrapper,
  FormlyFieldConfig,
  FormlyModule,
} from '@ngx-formly/core';
import { FormlyFieldProps } from '@ngx-formly/primeng/form-field';
import { InputTextModule } from 'primeng/inputtext';

interface CollapseWrapperProps extends FormlyFieldProps {
  collapseTitle?:
    | string
    | ((this: void, field: FormlyFieldConfig) => string | undefined);
  collapsed?: boolean;
}

export interface FormlyCollapseWrapperFieldConfig
  extends FormlyFieldConfig<CollapseWrapperProps> {}

export function defaultWrapperTitle(field: FormlyFieldConfig) {
  const index = Number.parseInt(field?.key as string, 10);

  if (Number.isNaN(index)) {
    return field?.key?.toString();
  }

  return `#${index + 1}`;
}

@Component({
    selector: 'kon-collapse-wrapper',
    imports: [CommonModule, FormlyModule, ReactiveFormsModule, InputTextModule],
    template: `
    <div
      [ngClass]="{
        'h-full flex items-center': collapsed(),
      }"
    >
      <div
        class="w-full flex items-center cursor-pointer space-x-3 mb-4"
        (click)="toggle()"
      >
        <i
          class="pi"
          [ngClass]="{
            'pi-chevron-down': collapsed(),
            'pi-chevron-up': !collapsed(),
          }"
        ></i>
        <h4 class="font-semibold text-gray-600">
          {{ title }}
        </h4>
      </div>

      <div [hidden]="collapsed()">
        <ng-container #fieldComponent></ng-container>
      </div>
    </div>
  `,
    styles: [],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormlyCollapseWrapperComponent
  extends FieldWrapper<FieldTypeConfig<CollapseWrapperProps>>
  implements OnInit
{
  override defaultOptions?:
    | Partial<FieldTypeConfig<CollapseWrapperProps>>
    | undefined = {
    props: {
      collapseTitle: defaultWrapperTitle,
    },
  };

  collapsed = signal(false);

  get title() {
    const { collapseTitle: title } = this.field.props || {};

    if (!title) {
      return defaultWrapperTitle(this.field);
    }

    if (typeof title === 'function') {
      return title(this.field);
    }

    return title;
  }

  toggle() {
    this.collapsed.set(!this.collapsed());
  }

  ngOnInit(): void {
    this.collapsed.set(this.field?.props?.collapsed ?? false);
  }
}
